import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ColumnFunctionMap, IDropdownDatasourceService, UrlColumnFunction } from "./interfaces";
import { Injectable, Injector } from "@angular/core";
import { LocalService } from "@app/@core/services/local.service";

@Injectable({
  providedIn: 'root'
})
export class DropdownDatasourceService implements IDropdownDatasourceService {

  private http: HttpClient;
  private sessionStorage: LocalService;
  private dropdownDataSources: Map<string, any[]> = new Map();
  private dropdownOptions: ColumnFunctionMap = {
    'gender': {
    dataSourceType: 'static',
    options: [
      { label: 'Male', value: 'male' },
      { label: 'Female', value: 'female' }
    ]
    },
    'task_group': {
      dataSourceType: 'url',
      httpMethod: 'GET',
      headers: [this.getHttpAuthHeaders()],
      dataSourceUrl: this.getUrlWithHostAppended(`platform/master/lookup/category/TASK_GROUP`),
      idPath: 'key',
      dataPath: 'data',
      labelProperty: 'value',
      valueProperty: 'value'
    }
  }

  constructor (injector: Injector,) {
    this.http = injector.get<HttpClient>(HttpClient);
    this.sessionStorage = injector.get<LocalService>(LocalService);
  }

  private getUrlWithHostAppended(url: string): string{
    const apiHost = this.sessionStorage?.getItem("apiHost");
    if (!apiHost) {
      return ``;
    }
    return `${apiHost}/${url}`;
  }

  private getHttpAuthHeaders(){
    const token = this.sessionStorage?.getItem('jwt-token');
    if (!token) {
      return null;
    }
    return { key: 'authorization', value: `Bearer ${token}` };
  }
  private async getDropdownOptions(columnDef: string): Promise<any[]> {
    let config = this.dropdownOptions[columnDef];
    if (!config) {
      const columnName = columnDef?.includes('.') ? columnDef.split('.').pop() : columnDef;
      config = this.dropdownOptions[columnName];
    }
    if (!config) return [];
        
    if (config.dataSourceType === 'static') {
      return config.options || [];
    } 
    else if (config.dataSourceType === 'url') {
      const url = config.dataSourceUrl;
      const headers = {};
      if (config.headers && Array.isArray(config.headers)) {
        config.headers.forEach(header => {
          headers[header.key] = header.value;
        });
      }
      
      try {
        const response = await this.http.request(
          config.httpMethod || 'GET', 
          url, 
          {headers: new HttpHeaders(headers)}
        ).toPromise();
        
        let data = response;
        if (config.dataPath) {
          const paths = config.dataPath.split('.');
          for (const path of paths) {
            data = data[path];
            if (!data) break;
          }
        }
                 
        if (Array.isArray(data)) {
          return data.map(item => ({
            label: this.getNestedValue(item, (config as UrlColumnFunction).labelProperty),
            value: this.getNestedValue(item, (config as UrlColumnFunction).valueProperty)
          }));
        }
        return [];
      } catch (error) {
        console.error('Error fetching dropdown options:', error);
        if (error.status === 401 || error.status === 403) {
          console.warn('Authentication/Authorization error when fetching dropdown options');
        } else if (error.status >= 500) {
          console.warn('Server error when fetching dropdown options');
        } else if (error.name === 'TimeoutError') {
          console.warn('Request timed out when fetching dropdown options');
        }
        return [];
      }
    }
        
    return [];
  }

  private getNestedValue(obj: any, path: string): any {
    if (!path) return obj;
    const parts = path.split('.');
    let value = obj;
    for (const part of parts) {
      if (value === null || value === undefined) return null;
      value = value[part];
    }
    return value;
  }

  private async loadDropdownOptionsForColumn(columnDef: string) {
    if (!columnDef) return;
    if (!this.dropdownDataSources.has(columnDef)) {
      const options = await this.getDropdownOptions(columnDef);
      this.dropdownDataSources.set(columnDef, options);
    }
  }

  initializeDropdowns(columns: any) {
    columns?.forEach(column => {
      if (column?.columnDef && column?.editInline && column?.editInlineComponentType === 'dropdown') {
        this.loadDropdownOptionsForColumn(column?.columnDef);
      }
    });
  }

  getDropdownDataSource(columnDef: string): any[] {
    return this.dropdownDataSources.get(columnDef) || [];
  }

}